import { useNavigate } from 'react-router-dom';
import { SearchItem } from '../types';
import { useEffect, useRef } from 'react';
import { compareIdents } from '../helpers/searchHelpers';
import updateParams from '../../utils/updateParams';

interface UseSearchNavigationProps {
  searchItems: SearchItem[];
  searchParams: URLSearchParams;
  isLoading: boolean;
}

export const useSearchNavigation = ({
  searchItems,
  searchParams,
  isLoading,
}: UseSearchNavigationProps) => {
  const navigate = useNavigate();
  const isNavigatingRef = useRef(false);
  const lastProcessedItemsRef = useRef<string>('');


  useEffect(() => {
    if (isLoading || isNavigatingRef.current) return;

    if (!['/examples', '/search'].includes(location.pathname)) {
      return;
    }

    const prevUrlIdents = searchParams.get('idents')?.split('-');
    const newIdents = searchItems.map(item => item.id);
    const currentItemsKey = newIdents.join('-');

    if (lastProcessedItemsRef.current === currentItemsKey) return;

    if (prevUrlIdents && compareIdents(prevUrlIdents, newIdents)) {
      lastProcessedItemsRef.current = currentItemsKey;
      return;
    }

    if ((prevUrlIdents?.length || 0) < searchItems.length) {
      lastProcessedItemsRef.current = currentItemsKey;
      return;
    }

    if ((prevUrlIdents?.length || 0) > searchItems.length) {
      isNavigatingRef.current = true;
      lastProcessedItemsRef.current = currentItemsKey;

      let newParams: string;

      const oldView = searchParams.get('view');
      if (searchItems.length !== 0) {
        newParams = updateParams({ searchItems });
      } else if (oldView === 'ALL') {
        // If the view is ALL with empty searchItems, this is a back or forward nav to "Browse All"
        // no need to update url
        return;
      } else {
        // else, the final searchItem was removed
        // we should nav to the correct tab in browse all view
        newParams = updateParams({
          view: 'ALL',
          tab: oldView as string | undefined,
          searchItems: [{ id: 'ALL' }],
        });
      }

      setTimeout(() => {
        navigate({ pathname: location.pathname, search: newParams });
        isNavigatingRef.current = false;
      }, 0);
    } else {
      lastProcessedItemsRef.current = currentItemsKey;
    }
  }, [searchItems, isLoading, navigate, searchParams]);

  useEffect(() => {
    return () => {
      isNavigatingRef.current = false;
    };
  }, []);
};
